import React, { useState, useEffect, useCallback } from 'react';
import { threadsAPI, projectAPI, structureAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ThreadList from './ThreadList';
import ThreadConversation from './ThreadConversation';
import CreateThreadDialog from './CreateThreadDialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Plus, Search, MessageSquare, Sparkles } from 'lucide-react';
import { DESIGN } from '../../components/DesignSystem';

const Threads = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  // Fetch threads
  const fetchThreads = useCallback(async () => {
    try {
      const response = await threadsAPI.getAll();
      setThreads(response.data.threads || []);
    } catch (error) {
      console.error('Error fetching threads:', error);
      toast.error('Failed to load threads');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch departments and projects for thread creation
  const fetchOptions = useCallback(async () => {
    try {
      const [deptRes, projRes] = await Promise.all([
        structureAPI.getDepartments(),
        projectAPI.getAll()
      ]);
      setDepartments(deptRes.data.departments || []);
      setProjects(projRes.data.projects || []);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  }, []);

  useEffect(() => {
    fetchThreads();
    fetchOptions();
  }, [fetchThreads, fetchOptions]);

  // Handle thread selection
  const handleSelectThread = useCallback(async (thread) => {
    setSelectedThread(thread);
    // Mark as read
    try {
      await threadsAPI.markRead(thread.id);
      // Update thread in list to clear unread count
      setThreads(prev => prev.map(t => 
        t.id === thread.id ? { ...t, unreadCount: 0 } : t
      ));
    } catch (error) {
      console.error('Error marking thread as read:', error);
    }
  }, []);

  // Handle thread creation
  const handleCreateThread = async (threadData) => {
    try {
      const response = await threadsAPI.create(threadData);
      const newThread = response.data.thread;
      setThreads(prev => [newThread, ...prev]);
      setSelectedThread(newThread);
      setCreateDialogOpen(false);
      toast.success('Thread created');
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to create thread');
    }
  };

  // Handle message sent - refresh thread activity
  const handleMessageSent = useCallback(() => {
    // Update thread's last activity
    if (selectedThread) {
      setThreads(prev => {
        const updated = prev.map(t => 
          t.id === selectedThread.id 
            ? { ...t, lastActivityAt: new Date().toISOString(), messageCount: t.messageCount + 1 }
            : t
        );
        // Re-sort by last activity
        return updated.sort((a, b) => new Date(b.lastActivityAt) - new Date(a.lastActivityAt));
      });
    }
  }, [selectedThread]);

  // Generate demo threads
  const [generating, setGenerating] = useState(false);
  
  const handleGenerateDemo = async () => {
    try {
      setGenerating(true);
      const response = await threadsAPI.generateDemo();
      toast.success(response.data.message || 'Demo threads created!');
      // Refresh threads list
      await fetchThreads();
    } catch (error) {
      console.error('Error generating demo:', error);
      toast.error('Failed to generate demo threads');
    } finally {
      setGenerating(false);
    }
  };

  // Filter threads by search
  const filteredThreads = threads.filter(thread =>
    thread.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    thread.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if user can post in selected thread
  const canPost = selectedThread && (
    selectedThread.type !== 'system' || user?.role === 'admin' || user?.role === 'owner'
  );

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="threads-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '${DESIGN.colors.textPrimary}' }}>Threads</h1>
          <p className="text-sm" style={{ color: '${DESIGN.colors.textMuted}' }}>
            Structured team communication
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleGenerateDemo}
            disabled={generating}
            data-testid="generate-demo-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" style={{ color: '#F59E0B' }} />
            {generating ? 'Generating...' : 'Generate Demo'}
          </Button>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            style={{ backgroundColor: '${DESIGN.colors.primary}', color: '#FFFFFF' }}
            data-testid="create-thread-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Thread
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className="flex-1 flex gap-0 rounded-xl overflow-hidden"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
      >
        {/* Thread List Panel */}
        <div 
          className="w-80 flex-shrink-0 flex flex-col"
          style={{ borderRight: '1px solid #E5E7EB', backgroundColor: '#FAFBFC' }}
        >
          {/* Search */}
          <div className="p-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
              <Input
                placeholder="Search threads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="thread-search"
              />
            </div>
          </div>

          {/* Thread List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div 
                  className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: '${DESIGN.colors.primary}', borderTopColor: 'transparent' }}
                />
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                <MessageSquare className="w-12 h-12 mb-3" style={{ color: '#D1D5DB' }} />
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  {searchQuery ? 'No threads match your search' : 'No threads yet'}
                </p>
              </div>
            ) : (
              <ThreadList
                threads={filteredThreads}
                selectedThreadId={selectedThread?.id}
                onSelectThread={handleSelectThread}
                currentUserId={user?.id}
              />
            )}
          </div>
        </div>

        {/* Conversation Panel */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedThread ? (
            <ThreadConversation
              thread={selectedThread}
              currentUser={user}
              canPost={canPost}
              onMessageSent={handleMessageSent}
              onThreadUpdate={(updated) => {
                setSelectedThread(updated);
                setThreads(prev => prev.map(t => t.id === updated.id ? updated : t));
              }}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center" style={{ backgroundColor: '#FAFBFC' }}>
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: '#E8F4F2' }}
              >
                <MessageSquare className="w-8 h-8" style={{ color: '${DESIGN.colors.primary}' }} />
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: '${DESIGN.colors.textPrimary}' }}>
                Select a thread
              </h3>
              <p className="text-sm text-center max-w-xs" style={{ color: '${DESIGN.colors.textMuted}' }}>
                Choose a thread from the list to view the conversation, or create a new one
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Thread Dialog */}
      <CreateThreadDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateThread}
        departments={departments}
        projects={projects}
      />
    </div>
  );
};

export default Threads;
